import {
  Button,
  HStack,
  Heading,
  Icon,
  Image,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  Tag,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";

import { BookingService } from "../services/booking.service";
import { IBooking } from "../types/types";
import { PiQrCode } from "react-icons/pi";
import QrModal from "./QrModal";
import { images } from "../assets/images/images";
import moment from "moment";
import { useState } from "react";

const bookingService = new BookingService();

const BookingCard: React.FC<{
  booking: IBooking;
  onSuccess?: () => void;
}> = ({ booking, onSuccess }) => {
  const toast = useToast();

  const {
    isOpen: isQrModalOpen,
    onOpen: onQrModalOpen,
    onClose: onQrModalClose,
  } = useDisclosure();

  const [bookingEndLoading, setBookingEndLoading] = useState<{
    [id: string]: boolean;
  }>({});

  const endBooking = async (id: string) => {
    setBookingEndLoading({ ...bookingEndLoading, [id]: true });

    bookingService
      .end(id)
      .then(({ data }) => {
        console.log("[endBooking] data", data);
        onSuccess && onSuccess();

        toast({
          title: "Reserva finalizada",
          description: "¡Tu reserva ha sido finalizada con éxito!",
          status: "success",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
      })
      .catch((err) => {
        console.log("[endBooking] err", err);

        toast({
          title: "Error al finalizar reserva",
          description:
            "No se ha podido finalizar la reserva. Inténtalo de nuevo más tarde.",
          status: "error",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
      })
      .finally(() => {
        setBookingEndLoading({ ...bookingEndLoading, [id]: false });
      });
  };

  return (
    <>
      <Stack
        key={booking.id}
        direction={{
          base: "column",
          md: "row",
        }}
        spacing={6}
        alignItems={"start"}
        w={"full"}
        px={6}
        py={4}
        borderRadius={"xl"}
        borderWidth={"2.5px"}
        borderColor={booking.status === "active" ? "brand.500" : "gray.200"}
      >
        <HStack spacing={6} w={"full"}>
          <Image
            src={images.locker_l_icon}
            alt={booking.locker_id}
            w={14}
            h={"auto"}
          />

          <VStack
            spacing={0}
            justifyContent={"start"}
            alignItems={"start"}
            w={"full"}
          >
            {/* Title */}
            <HStack justifyContent={"start"} alignItems={"center"} mb={2}>
              <Heading as="h2" fontSize="18px" lineHeight={1.1}>
                Locker {booking.locker_id}
              </Heading>
              {booking.status === "active" && (
                <Tag colorScheme="green" size="sm">
                  Activa
                </Tag>
              )}
            </HStack>

            {/* Date */}
            <VStack justifyContent={"start"} alignItems={"start"} spacing={1}>
              <Text color={"gray.800"} fontSize="sm" fontWeight={"medium"}>
                <>{moment(booking.start_time).format("DD MMM YY HH:mm")}</>
                <>
                  {booking.end_time
                    ? moment(booking.end_time).format("[ · ] DD MMM YY HH:mm")
                    : ` (${moment(booking.start_time)
                        .startOf("minute")
                        .fromNow()})`}
                </>
              </Text>
            </VStack>

            {/* Amount billed */}
            {booking.amount_billed && (
              <Text color={"gray.800"} fontSize="sm" fontWeight={"medium"}>
                €
                {booking.amount_billed.toLocaleString("es-ES", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Text>
            )}
          </VStack>
        </HStack>

        {/* Actions */}
        {booking.status === "active" && (
          <VStack
            justifyContent={"start"}
            alignItems={{ base: "center", md: "end" }}
            mb={2}
            w={{ base: "full", md: "auto" }}
          >
            <Button
              size={"md"}
              w={{ base: "full", md: "auto" }}
              colorScheme={"brand"}
              borderRadius={"full"}
              leftIcon={<Icon as={PiQrCode} color={"white"} boxSize={"6"} />}
              onClick={onQrModalOpen}
            >
              Abrir locker
            </Button>

            <Popover>
              <PopoverTrigger>
                <Button
                  size={"md"}
                  w={{ base: "full", md: "auto" }}
                  colorScheme={"gray"}
                  borderRadius={"full"}
                  fontWeight={"medium"}
                >
                  Finalizar reserva
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight={"semibold"}>
                  ¿Estás seguro?
                </PopoverHeader>
                <PopoverBody color={"gray.800"} fontSize={"sm"}>
                  Asegúrate de que has recogido todas tus pertenencias antes de
                  finalizar la reserva.
                </PopoverBody>
                <PopoverFooter as={HStack} justifyContent={"end"}>
                  <Button
                    size="sm"
                    colorScheme="brand"
                    isLoading={bookingEndLoading[booking.id]}
                    onClick={() => endBooking(booking.id)}
                  >
                    Confirmar
                  </Button>
                </PopoverFooter>
              </PopoverContent>
            </Popover>
          </VStack>
        )}
      </Stack>

      <QrModal
        booking_id={booking.id}
        isOpen={isQrModalOpen}
        onClose={() => {
          onSuccess && onSuccess();
          onQrModalClose();
        }}
      />
    </>
  );
};

export default BookingCard;
